/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useMemo } from 'react';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import path from 'path';
import { defineComponentMapping, getAllowedComponentsForNonAuthorMode } from '../import-components';
import {
  canUseDOM,
  CustomModelClient,
  getEnvProp,
  populatePIdataLayer,
  respGridUtil,
  UserSessionProvider,
} from '@marriott/mi-headless-utils';
import '../styles/styles.css';
import {
  useDCAPageModelStore,
  useClientEnvVarsStore,
  PageModelStoreType,
  PageModelState,
  createPageModelStore,
  PageModelContext,
} from '@marriott/mi-store-utils';
import { version } from '@marriott/global-styles/package.json';
import { setSubDirectoryPrefix, ACDLanalyticsWrapper } from '@marriott/mi-luxury-components';

// TODO: remove this after clientlibs issue fixed
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import 'react-loading-skeleton/dist/skeleton.css';

import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
    impressionArr: string[];
    impressionTrack: (value: string) => Record<string, string>;
    dataLayer?: Record<string, unknown>;
  }
}

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync();
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

/**
 * returns the lang-selector modal if it's present in top-nav
 * else return's undefined if there is no top-nav authored or if there is not lang selector autored ....
 */
const getLanguageSelectorModalFromTopNavigation = (model: any) => {
  const allItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'];

  // Find the headerResponsivegrid item
  const headerResponsivegrid =
    allItems?.['headerResponsivegrid']?.[':items']?.root?.[':items']?.responsivegrid?.[':items'];

  //added fallback if there is no headerResponsivegrid
  if (!headerResponsivegrid) return undefined;

  // Find the top-navigation item
  const topNavigation: any = Object.values(headerResponsivegrid).find((el: any) =>
    el?.[':type']?.includes('top-navigation')
  );

  //added fallback if there is no top-nav
  if (!topNavigation?.[':items']) return undefined;

  // Find the language-selector inside top-navigation
  return Object.values(topNavigation[':items']).find((el: any) => el?.[':type']?.includes('language-selector'));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = function (props: any) {
  const { Component, pageProps } = props;
  const { model, isAuthorMode, apolloEnvVars, currentLocale, NEXT_GLOBAL_ASSET_PREFIX, featureFlagLogo } = pageProps;

  const mergedPageProps = model?.prop_marsha_code
    ? { ...pageProps, serverDataLayer: { prop_marsha_code: model.prop_marsha_code } }
    : pageProps;
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const respGridModelClone = JSON.parse(JSON.stringify(respGridModel)); //To deep clone the model
  const store = useRef<PageModelStoreType>();
  const initialPageModel: PageModelState = {
    pageModel: respGridModel,
  };
  if (!store.current) {
    store.current = createPageModelStore(initialPageModel);
  }
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  if (setPageModel) {
    setPageModel(respGridModelClone);
  }
  const modelAllowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  // Wrap with useMemo to avoid recomputing on every render
  const allowedComponents = useMemo(() => {
    if (!model) return undefined;
    if ((modelAllowedComponents?.length ?? 0) > 0 || isAuthorMode) return modelAllowedComponents;
    return getAllowedComponentsForNonAuthorMode(model);
  }, [model, modelAllowedComponents, isAuthorMode]);

  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const languageSelectorModal = getLanguageSelectorModalFromTopNavigation(model);

  const envVars = {
    ...(apolloEnvVars ?? {}),
    NEXT_PUBLIC_ENABLE_WEBSDK: getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK') === 'true' && model?.enableWebSDK === 'true',
    NEXT_PUBLIC_SUBMIT_SEARCH_URL: mergedPageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: mergedPageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    NEXT_PUBLIC_APP_NAME: mergedPageProps?.NEXT_PUBLIC_APP_NAME,
    GOOGLE_MAP_API_KEY: mergedPageProps?.GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY: mergedPageProps?.BAIDU_MAP_API_KEY,
    FEATURE_FLAG_API_KEY_SERVER: mergedPageProps?.FEATURE_FLAG_API_KEY_SERVER,
    ACCEPT_LANGUAGE: currentLocale?.replace('_', '-'),
  };

  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);

  if (setEnvVars) {
    setEnvVars(envVars);
  }

  const isRtl = mergedPageProps?.model?.rtlEnable || mergedPageProps?.rtlEnable || false; // Default to LTR if not provided

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (isRtl ? `global-styles/${version}/marriot.global.rtl.css` : `global-styles/${version}/marriot.global.css`);
      document.head.appendChild(globalStyle);
    }
  }, []);

  if (canUseDOM) {
    populatePIdataLayer();
  }
  let subDirectoryContextPath = '';

  if (model) {
    subDirectoryContextPath = model?.subDirectoryContextPath ? model?.subDirectoryContextPath : '';
    setSubDirectoryPrefix(subDirectoryContextPath);
  }

  defineComponentMapping(allowedComponents, isAuthorMode, mergedPageProps, languageSelectorModal, featureFlagLogo);
  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });
  return (
    // For ACDL setup - @see https://marriottcloud.atlassian.net/browse/DXP-16772
    <UserSessionProvider pageProps={mergedPageProps} includePiData={true}>
      <PageModelContext.Provider value={store.current}>
        <ACDLanalyticsWrapper />
        <StyleSheetManager stylisPlugins={isRtl ? [rtlPluginTs] : []}>
          <Component {...mergedPageProps} />
        </StyleSheetManager>
      </PageModelContext.Provider>
    </UserSessionProvider>
  );
};

export default App;
